// API Service for Local Flyer Delivery
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================
export const authAPI = {
  login: (email, password) => 
    apiClient.post('/auth/login', { email, password }),
  
  register: (userData) => 
    apiClient.post('/auth/register', userData),
  
  getCurrentUser: () => 
    apiClient.get('/auth/me'),
  
  signContract: () =>
    apiClient.post('/auth/sign-contract'),
  
  signSafety: () =>
    apiClient.post('/auth/sign-safety'),
};

// ==================== JOBS ====================
export const jobsAPI = {
  // Get all available (open) jobs
  getAvailableJobs: () => 
    apiClient.get('/jobs'),
  
  // Get job details
  getJobDetail: (jobId) => 
    apiClient.get(`/jobs/${jobId}`),
  
  // Request a job (requires admin approval)
  requestJob: (jobId) =>
    apiClient.post(`/jobs/${jobId}/request`),
  
  // Cancel job request
  cancelJobRequest: (jobId) =>
    apiClient.post(`/jobs/${jobId}/cancel-request`),
  
  // Get my pending job requests
  getMyPendingRequests: () =>
    apiClient.get('/my-pending-requests'),
  
  // Get my job request history (all statuses: pending, approved, denied, cancelled)
  getMyRequestHistory: () =>
    apiClient.get('/my-request-history'),
  
  // Accept a job (user must be authenticated) - kept for backwards compatibility
  acceptJob: (jobId) => 
    apiClient.post(`/jobs/${jobId}/accept`),
  
  // Get user's active job (in_progress status)
  getMyActiveJob: () => 
    apiClient.get('/my-active-job'),
  
  // Update live GPS location during tracking
  updateLiveLocation: (jobId, locationData) =>
    apiClient.post(`/jobs/${jobId}/location`, locationData),
  
  // Clear live location when tracking stops
  clearLiveLocation: (jobId) =>
    apiClient.delete(`/jobs/${jobId}/location`),
  
  // Save tracking progress to server (for auto-save and manual sync)
  saveTrackingProgress: (jobId, progressData) =>
    apiClient.post(`/jobs/${jobId}/save-progress`, progressData),

  // Anti-cheat: report a delivery marked far from any house door
  reportDeliveryViolation: (data) =>
    apiClient.post('/tracking/delivery-violation', data),
  
  // Get saved tracking progress (for recovery after app restart)
  getTrackingProgress: (jobId) =>
    apiClient.get(`/jobs/${jobId}/get-progress`),
};

// ==================== MAP CORRECTIONS ====================
export const mapCorrectionsAPI = {
  // Submit missing house locations from manual tracking
  submitCorrection: (data) =>
    apiClient.post('/map-corrections', data),
};

// ==================== DELIVERY SESSIONS ====================
export const sessionsAPI = {
  // Create a new delivery session
  createSession: (sessionData) => 
    apiClient.post('/sessions', sessionData),
  
  // Get all my sessions (history)
  getMySessions: () => 
    apiClient.get('/my-sessions'),
  
  // Get user stats from sessions
  getMyStats: async () => {
    const response = await apiClient.get('/my-sessions');
    const sessions = response.data;
    
    // Calculate stats from sessions
    const totalDeliveries = sessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0);
    const totalEarnings = sessions.reduce((sum, s) => sum + (s.earnings || 0), 0);
    const totalDistance = sessions.reduce((sum, s) => sum + (s.distance || 0), 0);
    const totalDuration = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    
    // Calculate weekly stats (last 7 days)
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weeklySessions = sessions.filter(s => new Date(s.created_at) >= weekAgo);
    const weeklyDeliveries = weeklySessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0);
    const weeklyEarnings = weeklySessions.reduce((sum, s) => sum + (s.earnings || 0), 0);
    
    // Calculate days worked this week
    const daysWorkedSet = new Set();
    weeklySessions.forEach(s => {
      const date = new Date(s.created_at);
      daysWorkedSet.add(date.toDateString());
    });
    
    // Get weekly activity by day
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weeklyActivity = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStr = date.toDateString();
      const daySessions = weeklySessions.filter(s => 
        new Date(s.created_at).toDateString() === dayStr
      );
      const dayDeliveries = daySessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0);
      const dayEarnings = daySessions.reduce((sum, s) => sum + (s.earnings || 0), 0);
      const dayHours = daySessions.reduce((sum, s) => sum + (s.duration || 0), 0) / 3600;
      
      weeklyActivity.push({
        day: dayNames[date.getDay()],
        deliveries: dayDeliveries,
        earnings: dayEarnings,
        hours: Math.round(dayHours * 10) / 10,
        worked: daySessions.length > 0,
      });
    }
    
    return {
      totalJobs: sessions.length,
      totalDeliveries,
      totalEarnings: Math.round(totalEarnings * 100) / 100,
      totalDistance: Math.round(totalDistance * 10) / 10,
      totalHours: Math.round(totalDuration / 3600 * 10) / 10,
      weeklyDeliveries,
      weeklyEarnings: Math.round(weeklyEarnings * 100) / 100,
      daysWorked: daysWorkedSet.size,
      weeklyActivity,
      recentSessions: sessions.slice(0, 10),
    };
  },
};

// ==================== USER PROFILE ====================
export const userAPI = {
  // Get current user profile
  getProfile: () => 
    apiClient.get('/auth/me'),
  
  // Update user profile (would need backend endpoint)
  updateProfile: (userData) => 
    apiClient.put('/auth/me', userData),
};

// ==================== DELIVERY ZONES ====================
export const zonesAPI = {
  // Get all public zones
  getZones: () => 
    apiClient.get('/zones'),
};

// ==================== NOTIFICATIONS ====================
export const notificationsAPI = {
  // Get user notifications
  getNotifications: (unreadOnly = false, limit = 50) => 
    apiClient.get('/notifications', { params: { unread_only: unreadOnly, limit } }),
  
  // Mark single notification as read
  markAsRead: (notificationId) => 
    apiClient.put(`/notifications/${notificationId}/read`),
  
  // Mark all notifications as read
  markAllAsRead: () => 
    apiClient.put('/notifications/read-all'),
};

// ==================== ADMIN NOTIFICATIONS ====================
export const adminNotificationsAPI = {
  // Get admin notifications
  getNotifications: (unreadOnly = false, limit = 50) => 
    apiClient.get('/admin/notifications', { params: { unread_only: unreadOnly, limit } }),
  
  // Mark single notification as read
  markAsRead: (notificationId) => 
    apiClient.put(`/admin/notifications/${notificationId}/read`),
  
  // Mark all notifications as read
  markAllAsRead: () => 
    apiClient.put('/admin/notifications/read-all'),
};

// ==================== SUPPORT CHAT ====================
export const supportAPI = {
  // User endpoints
  getMessages: () => 
    apiClient.get('/support/messages'),
  
  sendMessage: (message) => 
    apiClient.post('/support/messages', { message }),
  
  getUnreadCount: () => 
    apiClient.get('/support/unread-count'),
};

// ==================== ADMIN SUPPORT CHAT ====================
export const adminSupportAPI = {
  // Get all conversations
  getConversations: () => 
    apiClient.get('/admin/support/conversations'),
  
  // Get single conversation
  getConversation: (userId) => 
    apiClient.get(`/admin/support/conversations/${userId}`),
  
  // Reply to conversation
  reply: (userId, message) => 
    apiClient.post(`/admin/support/conversations/${userId}/reply`, { message }),
  
  // Get total unread count
  getUnreadTotal: () => 
    apiClient.get('/admin/support/unread-total'),
};

// ==================== ADMIN MAP CORRECTIONS ====================
export const adminMapCorrectionsAPI = {
  // Get all map corrections
  getAll: (status) => 
    apiClient.get('/admin/map-corrections', { params: { status } }),
  
  // Get single correction detail
  getDetail: (archiveId) =>
    apiClient.get(`/admin/map-corrections/${archiveId}`),
  
  // Review/update correction status
  review: (archiveId, status, reviewNotes) =>
    apiClient.put(`/admin/map-corrections/${archiveId}/review`, null, { 
      params: { status, review_notes: reviewNotes } 
    }),
  
  // Delete correction
  delete: (archiveId) =>
    apiClient.delete(`/admin/map-corrections/${archiveId}`),
};

export default apiClient;
